import { Injectable, inject, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { VigimareIndication } from '../models/vigimare-indication.model';
import { VigimareVesselResponse } from '../models/vigimare-vessel.model';
import { VigimareObjectResponse } from '../models/vigimare-object.model';
import { environment } from '../../environments/environment';
import { KeycloakService } from '../security/keycloak.service';
import { fetchEventSource } from '@microsoft/fetch-event-source';

@Injectable({
  providedIn: 'root'
})
export class VigimareIndicationService {
  private http = inject(HttpClient);
  private zone = inject(NgZone);
  private keycloakService = inject(KeycloakService);

  private readonly indicationsApiUrl = `${environment.apiBaseUrl}/vigimare-indications`;
  private readonly vesselsApiUrl = `${environment.apiBaseUrl}/vigimare-vessels`;
  private readonly objectsApiUrl = `${environment.apiBaseUrl}/vigimare-objects`;

  getAll(): Observable<VigimareIndication[]> {
    return this.http.get<VigimareIndication[]>(this.indicationsApiUrl);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.indicationsApiUrl}/${id}`);
  }

  getVesselById(id: number): Observable<VigimareVesselResponse> {
    return this.http.get<VigimareVesselResponse>(`${this.vesselsApiUrl}/${id}`);
  }

  getObjectById(id: number): Observable<VigimareObjectResponse> {
    return this.http.get<VigimareObjectResponse>(`${this.objectsApiUrl}/${id}`);
  }

  listenForIndicationEvents(): Observable<{ type: 'created' | 'updated'; indication: VigimareIndication }> {
    return new Observable((observer) => {
      let destroyed = false;
      let currentAbort: AbortController | null = null;

      const connect = async () => {
        if (destroyed) return;

        currentAbort = new AbortController();

        try {
          const token = await this.keycloakService.updateToken();
          if (destroyed) return;

          await fetchEventSource(this.indicationsApiUrl + '/stream', {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
            signal: currentAbort.signal,

            // Without this the library aborts the stream whenever the tab is hidden and reopens it
            // on return — and nothing replays what was pushed in between. An indication arriving
            // while the operator was looking at another tab would simply never arrive.
            openWhenHidden: true,

            onmessage: (event) => {
              if (event.event === 'new-indication') {
                this.zone.run(() =>
                  observer.next({ type: 'created', indication: JSON.parse(event.data) })
                );
              }
              if (event.event === 'updated-indication') {
                this.zone.run(() =>
                  observer.next({ type: 'updated', indication: JSON.parse(event.data) })
                );
              }
            },

            onerror: (error) => {
              // Throw so fetchEventSource aborts this connection;
              // the catch block below schedules a reconnect with a fresh token.
              throw error;
            },
          });

          // Reached when the server ended the stream cleanly — the emitter's own timeout does this
          // every 30 minutes. The library only retries from its error path, so without this the
          // page goes quiet until someone reloads it.
          if (destroyed) return;
          setTimeout(connect, 5000);
        } catch {
          if (destroyed) return;
          // Reconnect after 5 s — refreshes the token each time so expiry is handled too.
          setTimeout(connect, 5000);
        }
      };

      connect();

      return () => {
        destroyed = true;
        currentAbort?.abort();
      };
    });
  }
}
